import { Popover } from "@headlessui/react";
import { mdiBell, mdiBellOutline } from "@mdi/js";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useExtensionContext } from "../../../../ExtensionProvider";
import { notifications as notificationsSelector } from "../../../../util/selectors";
import { IconButton } from "../IconButton";
import { NotificationItem } from "./NotificationItem";
import { useNotificationActions } from "./useNotificationActions";
import { useNotificationFiltering } from "./useNotificationFiltering";
import { useNotificationItems } from "./useNotificationItems";
/**
 * Inner component that receives popoverOpen as a prop so hooks can
 * react to it directly. The outer component just manages the Popover state.
 * This allows us to reset expand state and trigger auto-open when new notifications arrive.
 */
const NotificationsContent = ({ popoverOpen, }) => {
    const extensions = useExtensionContext();
    const api = extensions.getApi();
    const notifications = useSelector(notificationsSelector);
    const visibleCount = useMemo(() => notifications.filter((n) => n.type !== "silent").length, [notifications]);
    const buttonRef = useRef(null);
    const prevIdsRef = useRef(new Set(notifications.map((n) => n.id)));
    const [expand, setExpand] = useState(undefined);
    const filtered = useNotificationFiltering({
        notifications,
        open: popoverOpen,
    });
    const { dismissAll, suppress, triggerAction } = useNotificationActions({
        notifications,
        expand,
    });
    const handleExpandGroup = useCallback((groupId) => {
        setExpand(groupId);
    }, []);
    // Reset expand state when panel closes
    useEffect(() => {
        if (!popoverOpen && expand !== undefined) {
            setExpand(undefined);
        }
    }, [popoverOpen, expand]);
    // Auto-open popover when new notifications arrive.
    // Silent notifications should never open the tray.
    useEffect(() => {
        const currentIds = new Set(notifications.map((n) => n.id));
        const hasNew = notifications.some((n) => !prevIdsRef.current.has(n.id) && n.type !== "silent");
        prevIdsRef.current = currentIds;
        if (hasNew && !popoverOpen && buttonRef.current) {
            buttonRef.current.click();
        }
    }, [notifications, popoverOpen]);
    const { items, collapsed } = useNotificationItems({ filtered, expand });
    return (React.createElement(React.Fragment, null,
        React.createElement(Popover.Button, { as: IconButton, disabled: visibleCount === 0, iconPath: visibleCount > 0 ? mdiBell : mdiBellOutline, itemCount: visibleCount, ref: buttonRef, title: "Notifications", onClick: () => {
                api.events.emit("analytics-track-click-event", "Notifications", `${popoverOpen ? "Close" : "Open"} Notifications`);
            } }),
        popoverOpen && items.length > 0 && (React.createElement(Popover.Panel, { className: "absolute right-0 z-popover mt-2.5 max-h-[50vh] w-sm space-y-0.5 overflow-y-auto rounded-sm border border-stroke-weak bg-surface-base p-1 shadow-md" }, items.map((notification) => (React.createElement(NotificationItem, { collapsed: collapsed[notification.group], key: notification.id, notification: notification, onDismiss: dismissAll, onExpand: handleExpandGroup, onSuppress: suppress, onTriggerAction: triggerAction })))))));
};
export const Notifications = () => (React.createElement(Popover, { className: "relative" }, ({ open }) => React.createElement(NotificationsContent, { popoverOpen: open })));
